import { lumi } from "@/lib/lumi";
import type { JobAlert } from "@/hooks/useJobAlerts";
import { jobMatchesAlert } from "@/hooks/useJobAlerts";
import { emailCandidate_JobAlertMatches } from "@/lib/emails";
import type { ContractType, JobCategory } from "@/data/jobs";

/**
 * Service de notification des alertes emploi côté client.
 *
 * À la connexion d'un candidat, on parcourt ses alertes actives et, pour chaque
 * alerte, on regarde s'il y a de nouvelles offres `recruiter_jobs` (approuvées
 * et publiées récemment) qui correspondent aux critères et qui n'ont pas déjà
 * été notifiées (`lastNotifiedJobIds`).
 *
 * Si oui :
 *  - on envoie un email récapitulatif via `emailCandidate_JobAlertMatches`
 *  - on met à jour l'alerte avec `lastSentAt` et `lastNotifiedJobIds`
 *
 * Best-effort : tout échec est loggé sans bloquer l'application.
 */

const STORAGE_KEY_LAST_CHECK = "bnssa-mns:job-alerts:last-check";

interface RecruiterJobLite {
  _id: string;
  title: string;
  employer?: string;
  location?: string;
  department?: string;
  region?: string;
  category: JobCategory;
  contractType: ContractType;
  description?: string;
  validationStatus?: string;
  publishedAt?: string;
  createdAt?: string;
}

/** Fenêtre de fraîcheur pour considérer une offre comme "nouvelle".
 *  - immediate : 7 jours (on rattrape les offres récentes manquées entre 2 connexions)
 *  - daily     : 24h
 *  - weekly    : 7 jours
 *  La fréquence `immediate` était auparavant absente du test, ce qui pouvait
 *  donner des comportements incohérents ; on la gère désormais explicitement. */
function freshnessWindowMs(frequency: JobAlert["frequency"]): number {
  if (frequency === "daily") return 24 * 3600 * 1000;
  // immediate et weekly : 7 jours
  return 7 * 24 * 3600 * 1000;
}

/** Évite de spammer : on ne déclenche pas plus d'une fois toutes les 10 minutes
 *  côté client (assez court pour que les alertes « immediate » partent vite
 *  après la publication d'une annonce, sans pour autant boucler à chaque rendu). */
function canRunNow(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const last = window.localStorage.getItem(STORAGE_KEY_LAST_CHECK);
    if (!last) return true;
    const t = parseInt(last, 10);
    if (Number.isNaN(t)) return true;
    return Date.now() - t > 10 * 60 * 1000;
  } catch {
    return true;
  }
}

function markRun() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY_LAST_CHECK, String(Date.now()));
  } catch {
    // silent
  }
}

export async function checkAndNotifyJobAlerts(opts: {
  userId: string;
  userEmail?: string;
  userName?: string;
  /** Force l'exécution même si le throttle anti-spam (10 min) n'est pas écoulé.
   *  Utilisé lors de la création/modification d'une alerte pour un scan immédiat. */
  force?: boolean;
}): Promise<void> {
  if (!opts.userId || !opts.userEmail) return;
  if (!opts.force && !canRunNow()) {
    console.log("[jobAlerts] Skipped — déjà exécuté il y a moins de 10 minutes");
    return;
  }

  try {
    // 1) Charger les alertes actives du candidat.
    //    On cherche par `creator` (userId) OU `creatorEmail` afin d'être robuste
    //    si l'identifiant de session diffère légèrement de celui stocké à la
    //    création de l'alerte (cas observé sur le compte admin).
    const orConditions: Record<string, unknown>[] = [{ creator: opts.userId }];
    if (opts.userEmail) orConditions.push({ creatorEmail: opts.userEmail });
    const { list: alertsRaw } = await lumi.entities.job_alerts.list({
      filter: { isActive: true, $or: orConditions },
    });
    const alerts = (alertsRaw || []) as JobAlert[];
    if (alerts.length === 0) {
      console.log("[jobAlerts] Aucune alerte active trouvée pour", opts.userId, opts.userEmail);
      markRun();
      return;
    }
    console.log(`[jobAlerts] ${alerts.length} alerte(s) active(s) à évaluer`);

    // 2) Charger les offres recruteur récentes et approuvées
    const { list: jobsRaw } = await lumi.entities.recruiter_jobs.list({
      filter: { validationStatus: "approved" },
      sort: { createdAt: -1 },
      limit: 100,
    });
    const jobs = (jobsRaw || []) as RecruiterJobLite[];
    if (jobs.length === 0) {
      markRun();
      return;
    }

    // 3) Pour chaque alerte, filtrer les offres éligibles
    for (const alert of alerts) {
      try {
        const windowMs = freshnessWindowMs(alert.frequency);
        const sinceTs =
          alert.lastSentAt
            ? new Date(alert.lastSentAt).getTime()
            : Date.now() - windowMs;
        const alreadyNotified = new Set(alert.lastNotifiedJobIds || []);

        const matches = jobs.filter((j) => {
          // pas déjà notifié
          if (alreadyNotified.has(j._id)) return false;
          // récent
          const created = j.createdAt ? new Date(j.createdAt).getTime() : 0;
          if (created < sinceTs) return false;
          // matche les critères
          return jobMatchesAlert(
            {
              id: j._id,
              title: j.title,
              description: j.description,
              location: j.location,
              department: j.department,
              region: j.region,
              category: j.category,
              contractType: j.contractType,
            },
            alert
          );
        });

        if (matches.length === 0) continue;

        // 4) Envoyer l'email
        await emailCandidate_JobAlertMatches({
          candidateEmail: opts.userEmail,
          candidateName: opts.userName,
          alertName: alert.name,
          jobs: matches.map((j) => ({
            id: j._id,
            title: j.title,
            employer: j.employer || "",
            location: j.location || "",
            category: j.category,
            contractType: j.contractType,
          })),
        });

        // 5) Mettre à jour l'alerte (lastSentAt + lastNotifiedJobIds)
        const newNotifiedIds = Array.from(
          new Set([...(alert.lastNotifiedJobIds || []), ...matches.map((m) => m._id)])
        ).slice(-200); // garde un historique borné

        await lumi.entities.job_alerts.update(alert._id, {
          lastSentAt: new Date().toISOString(),
          lastNotifiedJobIds: newNotifiedIds,
          updatedAt: new Date().toISOString(),
        });

        console.log(
          `[jobAlerts] Email envoyé pour l'alerte "${alert.name}" — ${matches.length} offre(s)`
        );
      } catch (innerErr) {
        console.warn("[jobAlerts] Échec sur une alerte", alert._id, innerErr);
      }
    }

    markRun();
  } catch (err) {
    console.warn("[jobAlerts] Échec global du check des alertes", err);
  }
}
